import { useCallback, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Home, PartyPopper, Plane, Utensils } from 'lucide-react'

import {
    CollectMoneyScene,
    SharedExpensesScene,
    SplitBillScene,
    TrackExpensesScene,
} from './auth-intro-scenes'

import './auth-intro-screen.css'

const slideDurationMs = 6500

export interface IntroSlide {
    id: number
    icon: React.ReactNode
    tagline: string
    headline: string
    scene: React.ReactNode
}

const slides: IntroSlide[] = [
    {
        id: 0,
        icon: <Utensils className="auth-intro-copy-icon" />,
        tagline: 'Dining out?',
        headline: 'Split the bill\nin seconds',
        scene: <SplitBillScene />,
    },
    {
        id: 1,
        icon: <Plane className="auth-intro-copy-icon" />,
        tagline: 'Group trips?',
        headline: 'Track every\nexpense together',
        scene: <TrackExpensesScene />,
    },
    {
        id: 2,
        icon: <Home className="auth-intro-copy-icon" />,
        tagline: 'Living together?',
        headline: 'Shared expenses,\nzero drama',
        scene: <SharedExpensesScene />,
    },
    {
        id: 3,
        icon: <PartyPopper className="auth-intro-copy-icon" />,
        tagline: 'Planning events?',
        headline: 'Collect money\neffortlessly',
        scene: <CollectMoneyScene />,
    },
]

interface Props {
    onSignIn: () => void
}

export function AuthIntroScreen({ onSignIn }: Props) {
    const [currentSlide, setCurrentSlide] = useState(0)

    const nextSlide = useCallback(() => {
        setCurrentSlide((previousSlide) => (previousSlide + 1) % slides.length)
    }, [])

    useEffect(() => {
        const timer = window.setInterval(nextSlide, slideDurationMs)
        return () => window.clearInterval(timer)
    }, [nextSlide])

    const goToSlide = useCallback((index: number) => {
        setCurrentSlide(index)
    }, [])

    const activeSlide = slides[currentSlide]

    return (
        <div className="auth-intro">
            <div className="auth-intro-scene-wrap">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeSlide.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.35, ease: 'easeInOut' }}
                        className="auth-intro-scene-frame"
                    >
                        <div className="auth-intro-scene-scale-wrap">
                            {activeSlide.scene}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="auth-intro-bottom">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={`copy-${activeSlide.id}`}
                        initial={{ y: 12, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -12, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="auth-intro-copy-pill">
                            {activeSlide.icon}
                            <span>{activeSlide.tagline}</span>
                        </div>
                        <h1 className="auth-intro-headline">{activeSlide.headline}</h1>
                    </motion.div>
                </AnimatePresence>

                <div className="auth-intro-dots" aria-label="Onboarding Slides">
                    {slides.map((slide, index) => (
                        <button
                            key={slide.id}
                            type="button"
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                            className="auth-intro-dot"
                            data-active={index === currentSlide}
                            data-complete={index < currentSlide}
                        >
                            <span className="auth-intro-dot-track" />
                            {(index < currentSlide) && <span className="auth-intro-dot-fill" />}
                            {(index === currentSlide) && (
                                <motion.span
                                    className="auth-intro-dot-fill auth-intro-dot-fill--animated"
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ duration: slideDurationMs / 1000, ease: 'linear' }}
                                />
                            )}
                        </button>
                    ))}
                </div>

                <div className="auth-intro-access-note">
                    <p>Already have an account? Sign in to continue.</p>
                    <p>We are not accepting new accounts at this time. Keep an eye on our social media for updates!</p>
                </div>

                <motion.button
                    whileTap={{ scale: 0.985 }}
                    onClick={onSignIn}
                    type="button"
                    className="auth-intro-cta auth-intro-cta--primary"
                >
                    Sign In
                </motion.button>
            </div>
        </div>
    )
}
